import { useState, useEffect } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  LayoutDashboard, 
  Users, 
  Activity, 
  FileText, 
  Video, 
  Brain, 
  GitCompare,
  Settings,
  ChevronLeft,
  ChevronRight,
  Search,
  Moon,
  Sun,
  Plus,
  Globe,
  ChevronDown,
  Share2,
  Building2,
  Shield,
  LogOut,
  User,
  Home,
  Stethoscope,
  Syringe,
  TrendingUp,
  Target,
  Heart,
  Workflow,
  Sparkles
} from "lucide-react";
import { cn } from "../lib/utils";
import BrandRegionSelector from "./layout/BrandRegionSelector";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "./ui/dropdown-menu";
import { useBrand, BRAND_CONFIG } from "../context/BrandContext";
import { useNotifications } from "../context/NotificationContext";
import NotificationBell from "./NotificationBell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGION_CONFIG = {
  UAE: { flag: "🇦🇪", color: "text-emerald-500" },
  Oman: { flag: "🇴🇲", color: "text-red-500" },
  KSA: { flag: "🇸🇦", color: "text-green-500" },
};

const navSections = [
  {
    title: "Overview",
    items: [
      { path: "/", icon: LayoutDashboard, label: "Dashboard" },
    ]
  },
  {
    title: "Intelligence",
    items: [
      { path: "/competitors", icon: Users, label: "Competitors" },
      { path: "/intelligence", icon: Activity, label: "Reports" },
      { path: "/social", icon: Share2, label: "Social Media" },
      { path: "/battlecards", icon: Shield, label: "Battlecards" },
    ]
  },
  {
    title: "Advanced Analytics",
    items: [
      { path: "/trends", icon: TrendingUp, label: "Trends & Alerts" },
      { path: "/market-position", icon: Target, label: "Market Position" },
      { path: "/sentiment", icon: Heart, label: "Sentiment Analysis" },
    ]
  },
  {
    title: "Automation & AI",
    items: [
      { path: "/automation", icon: Workflow, label: "Workflow Rules" },
      { path: "/predictive", icon: Sparkles, label: "Predictive AI" },
      { path: "/algorithmic", icon: Brain, label: "Algo Intelligence" },
      { path: "/cross-brand", icon: GitCompare, label: "Cross-Brand Momentum" },
    ]
  },
  {
    title: "Content",
    items: [
      { path: "/content", icon: FileText, label: "Content Studio" },
      { path: "/videos", icon: Video, label: "Video Studio" },
    ]
  },
  {
    title: "Analytics",
    items: [
      { path: "/learning", icon: Brain, label: "Insights" },
      { path: "/settings", icon: Settings, label: "Settings" },
    ]
  },
  {
    title: "Admin",
    items: [
      { path: "/admin", icon: Shield, label: "Admin Settings", adminOnly: true },
    ]
  }
];

export default function Layout({ user, onLogout }) {
  const { brand, switchBrand, config: brandConfig, userScope, canSwitchBrand, canSwitchRegion, lockedRegion } = useBrand();
  const [collapsed, setCollapsed] = useState(false);
  const [darkMode, setDarkMode] = useState(() => brandConfig.isDark);
  const [region, setRegion] = useState(() => {
    // Honour locked region from user scope; otherwise fall back to localStorage
    const stored = localStorage.getItem('app_region') || 'UAE';
    let userScopeRegion = null;
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null');
      userScopeRegion = u?.region || null;
    } catch (_e) { /* ignore */ }
    return userScopeRegion || stored;
  });
  const [regions, setRegions] = useState(['UAE', 'Oman', 'KSA']);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get notification subscription function
  const { subscribeToBrand } = useNotifications();

  // Update dark mode when brand changes
  useEffect(() => {
    setDarkMode(brandConfig.isDark);
  }, [brandConfig.isDark]);
  
  // Update WebSocket subscription when brand changes
  useEffect(() => {
    subscribeToBrand(brand);
  }, [brand, subscribeToBrand]);

  useEffect(() => {
    // Fetch available regions
    axios.get(`${API}/regions`).then(res => {
      setRegions(res.data.regions || ['UAE', 'Oman', 'KSA']);
    }).catch(() => {});
  }, []);

  // If user is region-scoped, force region into sync with their scope
  useEffect(() => {
    if (lockedRegion && region !== lockedRegion) {
      setRegion(lockedRegion);
      localStorage.setItem('app_region', lockedRegion);
    }
  }, [lockedRegion, region]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.body.classList.toggle('dark-mode');
  };

  const handleRegionChange = (newRegion) => {
    if (!canSwitchRegion) return;  // scoped user — block switching
    setRegion(newRegion);
    localStorage.setItem('app_region', newRegion);
  };

  const handleBrandChange = (newBrand) => {
    switchBrand(newBrand);
  };

  const handleLogout = () => {
    if (onLogout) onLogout();
    navigate('/login');
  };

  const regionInfo = REGION_CONFIG[region] || REGION_CONFIG.UAE;
  const displayName = `${brandConfig.name} ${region}`;

  // Dynamic theme colors
  const theme = brandConfig.theme;

  return (
    <div 
      className="flex min-h-screen transition-colors duration-300"
      style={{ backgroundColor: theme.background }}
    >
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 h-screen z-50 transition-all duration-200",
          collapsed ? "w-16" : "w-64"
        )}
        style={{ 
          backgroundColor: darkMode ? '#010409' : theme.surface,
          borderRight: `1px solid ${theme.border}`
        }}
        data-testid="sidebar"
      >
        {/* Logo & Brand Switcher */}
        <div 
          className={cn(
            "flex items-center h-14",
            collapsed ? "justify-center px-2" : "px-4"
          )}
          style={{ borderBottom: `1px solid ${theme.border}` }}
        >
          <img 
            src={brandConfig.logo} 
            alt={brandConfig.name} 
            className={cn(
              "object-contain",
              collapsed ? "w-10 h-10" : "w-10 h-10"
            )}
            onError={(e) => {
              // Fallback to icon if logo fails to load
              e.target.style.display = 'none';
            }}
          />
          {!collapsed && (
            <span 
              className="ml-2 font-bold text-lg"
              style={{ color: theme.text }}
            >
              {brandConfig.name}
            </span>
          )}
        </div>

        {/* Brand & Region Selector */}
        {!collapsed && (
          <BrandRegionSelector
            brand={brand}
            brandConfig={brandConfig}
            brands={BRAND_CONFIG}
            region={region}
            regions={regions}
            regionConfig={REGION_CONFIG}
            theme={theme}
            darkMode={darkMode}
            displayName={displayName}
            handleBrandChange={handleBrandChange}
            handleRegionChange={handleRegionChange}
            canSwitchBrand={canSwitchBrand}
            canSwitchRegion={canSwitchRegion}
          />
        )}

        {/* Navigation */}
        <nav className="py-4 overflow-y-auto h-[calc(100vh-120px)] scrollbar-thin">
          {navSections.map((section, sectionIndex) => {
            // Filter items based on admin access
            const visibleItems = section.items.filter(item => {
              if (item.adminOnly && (!user || user.role !== 'admin')) {
                return false;
              }
              return true;
            });
            
            // Don't render section if no visible items
            if (visibleItems.length === 0) return null;
            
            return (
            <div key={section.title} className="mb-4">
              {!collapsed && (
                <div 
                  className="px-4 py-2 text-xs font-semibold uppercase tracking-wider"
                  style={{ color: theme.textMuted }}
                >
                  {section.title}
                </div>
              )}
              {visibleItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="flex items-center gap-3 mx-2 px-3 py-2 rounded-lg text-sm transition-colors"
                    style={{
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      backgroundColor: isActive ? `${theme.primary}15` : 'transparent',
                      color: isActive ? theme.primary : theme.textMuted,
                      fontWeight: isActive ? 500 : 400,
                    }}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" style={{ color: isActive ? theme.primary : theme.textMuted }} />
                    {!collapsed && <span>{item.label}</span>}
                  </NavLink>
                );
              })}
            </div>
          );
          })}
        </nav>

        {/* Collapse Toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          data-testid="sidebar-toggle"
          className="absolute bottom-4 -right-3 w-6 h-6 rounded-full flex items-center justify-center transition-colors"
          style={{
            backgroundColor: darkMode ? '#21262d' : theme.surface,
            border: `1px solid ${theme.border}`,
            color: theme.textMuted
          }}
        >
          {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
        </button>
      </aside>

      {/* Main Content */}
      <div className={cn("flex-1 transition-all duration-200", collapsed ? "ml-16" : "ml-64")}>
        {/* Top Header */}
        <header 
          className="sticky top-0 z-40 h-14 flex items-center justify-between px-6"
          style={{
            backgroundColor: darkMode ? 'rgba(13, 17, 23, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(8px)',
            borderBottom: `1px solid ${theme.border}`
          }}
        >
          {/* Search */}
          <div className="flex items-center flex-1 max-w-xl">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: theme.textMuted }} />
              <input
                type="text"
                placeholder="Search competitors, reports, content..."
                className="w-full pl-10 pr-4 py-2 text-sm rounded-lg transition-all"
                style={{
                  backgroundColor: darkMode ? '#21262d' : '#f1f5f9',
                  border: `1px solid ${theme.border}`,
                  color: theme.text,
                }}
                data-testid="global-search"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 ml-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleDarkMode}
              className="w-9 h-9 p-0"
              style={{ color: theme.textMuted }}
              data-testid="theme-toggle"
            >
              {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            {/* Notification Bell with WebSocket */}
            <NotificationBell darkMode={darkMode} />
            
            <Button
              className="text-white text-sm px-4 h-9"
              style={{ backgroundColor: theme.primary }}
              data-testid="new-scan-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              New Scan
            </Button>
            
            {/* User Menu */}
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex items-center gap-2 ml-2"
                    style={{ color: theme.text }}
                    data-testid="user-menu"
                  >
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${theme.primary}20` }}>
                      <User className="w-4 h-4" style={{ color: theme.primary }} />
                    </div>
                    <span className="text-sm font-medium hidden md:inline">{user.name}</span>
                    <ChevronDown className="w-4 h-4 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="end" 
                  className="w-48"
                  style={{ 
                    backgroundColor: darkMode ? '#161b22' : '#ffffff',
                    borderColor: theme.border 
                  }}
                >
                  <DropdownMenuLabel style={{ color: theme.textMuted }}>
                    <div className="flex flex-col">
                      <span style={{ color: theme.text }}>{user.name}</span>
                      <span className="text-xs font-normal">{user.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator style={{ backgroundColor: theme.border }} />
                  {user.role === 'admin' && (
                    <DropdownMenuItem
                      onClick={() => navigate('/admin')}
                      className="cursor-pointer"
                      style={{ color: theme.text }}
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Admin Settings
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="cursor-pointer text-red-500"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Outlet context={{ darkMode, region, brand, setRegion: handleRegionChange, theme }} />
        </main>
      </div>
    </div>
  );
}
